/**
 * 商品详情页面 - 支持图片轮播
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { productApi, type Product } from '@/services/productApi';
import { Loader2, ShoppingBag, ExternalLink, ChevronLeft, Package, ChevronLeft as ArrowLeft, ChevronRight as ArrowRight, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const data = await productApi.getProduct(productId!);
      setProduct(data);
    } catch (error) {
      console.error('加载商品详情失败:', error);
      toast.error('加载商品详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = () => {
    if (!product?.purchaseUrl) {
      toast.error('该商品暂未配置购买链接');
      return;
    }

    const url = product.purchaseUrl;
    console.log('[商品购买] 打开链接:', url);

    // 兼容 App 内嵌 WebView / 微信内置浏览器 / 部分国产浏览器对外部域名的限制。
    // window.open 和 a.click 在这些环境里跳转外部域名常被直接拦截（无反应），
    // location.href 同页跳转兼容性最好；新窗口方案失败时自动降级。
    const openInNewWindow = () => {
      try {
        const newWin = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWin) return true;
      } catch (e) {
        console.warn('[商品购买] window.open 异常', e);
      }
      // a 标签兜底（普通浏览器有效，WebView 通常仍无效）
      try {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return true;
      } catch (e) {
        console.warn('[商品购买] a.click 异常', e);
      }
      return false;
    };

    // WebView 环境检测：微信内置浏览器 / App WebView 等
    const ua = navigator.userAgent.toLowerCase();
    const isInAppWebView = /micromessenger|weixin|alipay|qq\/|webview|wv|FB_IAB|FB4A|instagram/i.test(ua)
      || (window as any).__wxjs_environment === 'miniprogram';

    if (isInAppWebView) {
      // WebView 内：window.open/a.click 对外部域名通常被拦截
      // 直接用 location.href 跳转（同页跳转兼容性最好，用户点返回可回到本页）
      console.log('[商品购买] 检测到 App/微信内置浏览器，使用 location.href 跳转');
      window.location.href = url;
      return;
    }

    // 普通浏览器：优先新窗口，失败降级 a 标签，再失败 location.href
    if (!openInNewWindow()) {
      console.warn('[商品购买] 新窗口打开失败，降级 location.href');
      window.location.href = url;
    }
  };

  // 上一张图片
  const handlePrevious = useCallback(() => {
    if (!product) return;
    const images = product.imageUrls && product.imageUrls.length > 0
      ? product.imageUrls
      : product.coverImageUrl
      ? [product.coverImageUrl]
      : [];
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  }, [product]);

  // 下一张图片
  const handleNext = useCallback(() => {
    if (!product) return;
    const images = product.imageUrls && product.imageUrls.length > 0
      ? product.imageUrls
      : product.coverImageUrl
      ? [product.coverImageUrl]
      : [];
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  }, [product]);

  // 自动播放
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(handleNext, 3000); // 每3秒切换一次
    return () => clearInterval(interval);
  }, [isPlaying, handleNext]);

  if (loading) {
    return (
      <LayoutWithSidebar title="商品详情">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-600">加载中...</span>
        </div>
      </LayoutWithSidebar>
    );
  }

  if (!product) {
    return (
      <LayoutWithSidebar title="商品详情">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Alert className="max-w-md">
            <AlertDescription>商品不存在</AlertDescription>
          </Alert>
        </div>
      </LayoutWithSidebar>
    );
  }

  const images = product.imageUrls && product.imageUrls.length > 0
    ? product.imageUrls
    : product.coverImageUrl
    ? [product.coverImageUrl]
    : [];

  return (
    <LayoutWithSidebar title="商品详情">
      <div className="min-h-screen bg-gray-50">
        {/* 页头 */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <button
              onClick={() => navigate('/mall')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
              返回商城
            </button>
          </div>
        </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* 左侧 - 图片轮播 */}
          <div>
            <Card>
              <CardContent className="p-0">
                {/* 主图区域 - 带轮播控制 */}
                <div className="relative aspect-square bg-gray-100">
                  {images.length > 0 ? (
                    <>
                      <img
                        src={images[currentImageIndex]}
                        alt={`${product.name} - 图片 ${currentImageIndex + 1}`}
                        className="w-full h-full object-contain"
                      />

                      {/* 左右箭头 */}
                      {images.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={handlePrevious}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-colors"
                            aria-label="上一张"
                          >
                            <ArrowLeft className="h-5 w-5 text-gray-700" />
                          </button>
                          <button
                            type="button"
                            onClick={handleNext}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-colors"
                            aria-label="下一张"
                          >
                            <ArrowRight className="h-5 w-5 text-gray-700" />
                          </button>
                        </>
                      )}

                      {/* 自动播放控制 */}
                      {images.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setIsPlaying(!isPlaying)}
                          className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-colors"
                          aria-label={isPlaying ? '暂停' : '播放'}
                        >
                          {isPlaying ? (
                            <Pause className="h-5 w-5 text-gray-700" />
                          ) : (
                            <Play className="h-5 w-5 text-gray-700" />
                          )}
                        </button>
                      )}

                      {/* 图片计数器 */}
                      {images.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-sm">
                          {currentImageIndex + 1} / {images.length}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="h-24 w-24 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* 缩略图列表 */}
                {images.length > 1 && (
                  <div className="flex gap-2 p-4 overflow-x-auto">
                    {images.map((image, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                          currentImageIndex === index
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${product.name} 缩略图 ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* 右侧 - 商品信息 */}
          <div className="space-y-6">
            {/* 基本信息 */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">{product.name}</CardTitle>
                    {product.subtitle && (
                      <CardDescription className="text-lg">{product.subtitle}</CardDescription>
                    )}
                  </div>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <Badge className="bg-red-500">
                      {Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 价格 */}
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-red-600">
                    ¥{product.price.toFixed(2)}
                  </span>
                  {product.originalPrice && (
                    <span className="text-xl text-gray-400 line-through">
                      ¥{product.originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>

                {/* 购买按钮 */}
                <Button
                  onClick={handlePurchase}
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <ExternalLink className="h-5 w-5 mr-2" />
                  立即购买
                </Button>

                {product.purchasePlatform && (
                  <p className="text-sm text-gray-500 text-center">
                    跳转至 {product.purchasePlatform} 完成购买
                  </p>
                )}
              </CardContent>
            </Card>

            {/* 商品规格 */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">商品规格</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-4">
                    {Object.entries(product.specs).map(([key, value]) => (
                      <div key={key} className="border-b pb-2">
                        <dt className="text-sm text-gray-600">{key}</dt>
                        <dd className="mt-1 font-medium">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* 特色功能 */}
            {product.features && product.features.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">特色功能</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Package className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* 商品描述 */}
            {product.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">商品详情</CardTitle>
                </CardHeader>
                <CardContent>
                  <MarkdownRenderer
                    content={product.description}
                    className="prose prose-sm max-w-none text-gray-700"
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      </div>
    </LayoutWithSidebar>
  );
}
